import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Drawer,
  Button,
  Upload,
  message,
  Select,
  Typography,
  Tooltip,
  Badge,
} from "antd";
import {
  DownOutlined,
  LeftOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import { IDocumentFields } from "../interfaces/ICase";
import {
  deleteUploadedDocument,
  downloadAllAttachments,
  downloadDocument,
  getAllStarhealthDocumentsData,
  getUnreadCount,
  updateAllDocumentsData,
} from "../apis/patientService";
import { errorMessage, successMessage } from "./notificationUtils";
import { AppDispatch, RootState } from "../store/store";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedFile, UploadDocument } from "../models/visitSlice";
import { fetchPreAuthStepper } from "../models/preAuthSlice";
import Chatter from "../components/patient/Chatter";
import ConfirmModals from "./confirmModals";
import PreviewModalForSecondDrawer from "../components/modals/PreviewModalForSecondDrawer";
import FloatingChatButton from "./floatingButton";
import { useLocation } from "react-router-dom";
import { errorMessageModal } from "./modalMessage";
import { ChatNotificationPayload, useChatSse } from "../hooks/useChatSse";
import { API_URL } from "./constants";
import { midOfficeRoleAbbreviations } from "./roleUtils";

const { Dragger } = Upload;
const { Option } = Select;
const { Title, Text } = Typography;

interface DocumentData {
  id: string;
  type: string;
  label: string;
  labelId: string;
  labelValues: string | null;
  visitDoa: string | null;
  required: boolean;
  data: string;
  documentName: string;
  deficiencyCheckStatus: string | null;
  remarks: string | null;
  documentId: number;
  caseDischargeId: string | null;
  caseClaimSubmissionId: string | null;
  caseEnhancementId: string | null;
  status: string;
  multiple: boolean;
  updatable: boolean;
  docList?: any;
}

type Document = {
  label: string;
  value: string;
};
interface FloatingDrawerProps {
  getBillList?: any;
  schemeMetaDataId: number | undefined;
  schemeMetaDataName: string | undefined;
  caseId: number | undefined;
  visitId: number | undefined;
  schemeData?: any;
  stepNumber?: number | undefined;
}

const FloatingDrawer: React.FC<FloatingDrawerProps> = (props) => {
  const location = useLocation();
  const prevPathnameRef = useRef(location.pathname);

useEffect(() => {
  // if (prevPathnameRef.current !== location.pathname) {
  //   localStorage.setItem("notificationCount", "0");
  //   prevPathnameRef.current = location.pathname;
  // }

  return () => {
    localStorage.setItem("notificationCount", "0");
  }
}, [location.pathname]);

  useEffect(() => {
    const handleRouteOrHashChange = () => {
      localStorage.setItem("notificationCount", "0");
    };
    window.addEventListener("popstate", handleRouteOrHashChange);
    window.addEventListener("hashchange", handleRouteOrHashChange);

    const handleBeforeUnload = () => {
      localStorage.setItem("notificationCount", "0");
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("popstate", handleRouteOrHashChange);
      window.removeEventListener("hashchange", handleRouteOrHashChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const dispatch = useDispatch<AppDispatch>();
  const [isChatCollapsed, setIsChatCollapsed] = useState(false);
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const isChatMinimizedRef = useRef(false);
  const isChatCollapsedRef = useRef(false);
  const { preAuth, hospital, patients, auth, visit } = useSelector(
    (state: RootState) => state
  );

  const rolesAbbreviation: any = {
    FO_STAFF: "FO",
    BO_STAFF: "BO",
    ADMIN: "ADMIN",
    ...midOfficeRoleAbbreviations,
    "Bill auditor": "BA",
    "Bill Approver": "BAP",
    "Portal Manager": "PM",
  };

  const userStr: any = localStorage.getItem("user");
  // const currentUserId = JSON.parse(userStr)?.id;
  // const currentUserId = localStorage.getItem("userName");
  const currentUserId =
    JSON.parse(userStr)?.firstname?.replace(/\s+/g, "-") +
    "-" +
    JSON.parse(userStr)?.lastname?.replace(/\s+/g, "-") +
    "-" +
    JSON.parse(userStr)?.id +
    "-" +
    (auth?.role?.role ? rolesAbbreviation[auth?.role?.role] : "");

  const handleGetUnreadNotification = async () => {
    try {
      const res = await getUnreadCount(patients?.fetchBasicDetails?.visitInfoResponse?.id, currentUserId);
      setUnreadTotal(res.unreadCount);
    }
    catch(err) {
      errorMessage("Please, Try again later");
    }
  }

  useEffect(() => {
    isChatMinimizedRef.current = isChatMinimized;
  }, [isChatMinimized]);

  useEffect(() => {
    isChatCollapsedRef.current = isChatCollapsed;
  }, [isChatCollapsed]);

  const [unreadTotal, setUnreadTotal] = useState<any>(
    parseInt(localStorage.getItem("notificationCount") ?? "0")
  );

  const [isNotificationPermissionGranted, setIsNotificationPermissionGranted] =
    useState(false);

  // Request notification permission
  useEffect(() => {
    if ("Notification" in window) {
      Notification.requestPermission().then((permission) => {
        setIsNotificationPermissionGranted(permission === "granted");
      });
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("notificationCount", unreadTotal);
  }, [unreadTotal]);

  const handleNotificationCountChange = useCallback((count: number) => {
    if (isChatCollapsedRef.current || isChatMinimizedRef.current) {
      if (count !== 0) {
        setUnreadTotal((prev: any) => prev + 1);
        // setUnreadTotal(count);
      }
    }
  }, []);

  // useEffect(() => {
  //   function handleUnread(e: Event) {
  //     const detail = (e as CustomEvent).detail as {
  //       type: "chat:notification:count";
  //       contextId?: string;
  //       count: number;
  //     };
  //     setUnreadTotal(detail.count);
  //   }

  //   window.addEventListener(
  //     "gseven:chat:unread",
  //     handleUnread as EventListener
  //   );
  //   return () =>
  //     window.removeEventListener(
  //       "gseven:chat:unread",
  //       handleUnread as EventListener
  //     );
  // }, []);

  const selectedFile = useSelector(
    (state: RootState) => state?.visit?.uploadDocument?.selectedFile
  );

  // States
  const [open, setOpen] = useState(false);
  const [secondDocument, setSecondDocument] = useState(false); // to store modal open for second document preview
  const [secondDocumentUrl, setSecondDocumentUrl] = useState(""); // to store document url of second document to preview

  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [expandedSection2, setExpandedSection2] = useState<string | null>(null);
  const [documentTypes, setDocumentTypes] = useState<{
    [key: string]: Array<DocumentData>;
  }>({});
  const [structuredDocumentList, setStructuredDocumentList] = useState<{
    [key: string]: Array<DocumentData>;
  }>({});
  const [saveBtnLoading, setSaveBtnLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedPreAuthStep, setSelectedPreAuthStep] = useState<
    string | undefined
  >("");
  const [selectedDocumentType, setSelectedDocumentType] =
    useState<Document | null>(null);
  const [uploadedDocuments, setUploadedDocuments] = useState<Document[]>([]);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState({
    url: "",
    stage: "",
    name: "",
    showUpload: false,
    documentType: "",
  });
  const [uploadedDocumentUrlList, setUploadedDocumentUrlList] = useState<
    Record<string, Array<IDocumentFields>>
  >({});
  const [uploadedDocumentUrl, setUploadedDocumentUrl] = useState<
    Record<string, Array<IDocumentFields>>
  >({});
  const [documentUrl, setDocumentUrl] = useState<string>("");
  const [previewDocument, setPreviewDocument] = useState(false);
  const [documentName, setDocumentName] = useState("");
  const [selectedDocument, setSelectedDocument] =
    useState<IDocumentFields | null>(null);
  const [previewDocumentUrl, setPreviewDocumentUrl] = useState("");
  const [deficiencyModal, setDeficiencyModal] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(
    null
  );
  const [chatterVisible, setIsChatterVisible] = useState(false);
  const [isDownloadingDocument, setIsDownloadingDocument] =
    useState<boolean>(false);
  const [downloadingDocumentUrl, setDownloadingDocumentUrl] =
    useState<string>("");
  const [isTimelineVisible, setIsTimelineVisible] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("gseven_chat_collapsed");
      if (stored) {
        setIsChatCollapsed(stored === "true");
      }
    } catch {}
  }, []);

  const toggleChatCollapsed = () => {
    setIsChatCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("gseven_chat_collapsed", String(next));
      } catch {}
      return next;
    });
  };

  const minimizeChat = () => {
    setIsChatMinimized(true);
    setIsChatterVisible(false);
  };

  const restoreChat = () => {
    setIsChatMinimized(false);
    setIsChatterVisible(true);
    setUnreadTotal(0);
    localStorage.setItem("notificationCount", "0");
  };

  const showTimeline = () => {
    setIsTimelineVisible(true); // Set state to show the Timeline component
  };

  const showChatter = () => {
    setIsChatterVisible(true); // Set state state
  };

  const closeTimeline = () => {
    setIsTimelineVisible(false); // Set state to hide the Timeline component
  };

  const closeChatter = () => {
    setIsChatterVisible(false); // Set state to hide the chatter component
  };

  const showDrawer = () => {
    setOpen(true);
  };
  const closeDrawer = () => {
    setOpen(false);
  };

  useEffect(() => {
    if(patients?.fetchBasicDetails?.visitInfoResponse?.id && currentUserId && !chatterVisible) handleGetUnreadNotification();
  }, [patients?.fetchBasicDetails?.visitInfoResponse?.id, currentUserId, isChatCollapsed, isChatMinimized, chatterVisible]);

  const getStageFromStepNumber = (
    stepNumber: number | undefined
  ): string | null => {
    if (!stepNumber) return null;

    const stepToStageMap: { [key: number]: string } = {
      2: "preauth",
      3: "preauthQuery",
      4: "enhancement",
      5: "discharge",
      6: "claim",
    };

    return stepToStageMap[stepNumber] || null;
  };

  useEffect(() => {
    if (open && props.stepNumber) {
      const stage = getStageFromStepNumber(props.stepNumber);
      if (stage) {
        setExpandedSection(stage);
      }
    }
  }, [open, props.stepNumber]);

  const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
  const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
  const currentUrl = window.location.href;

  useEffect(() => {
    if (
      // currentUrl.includes("visit-details") ||
      currentUrl.includes("billing-list")
    ) {
      if (caseId && visitId) {
        dispatch(fetchPreAuthStepper({ caseId: caseId, visitId: visitId }));
      }
    }
  }, [caseId, visitId]);

  const handleSseNotification = useCallback(
      (payload: ChatNotificationPayload) => {
        // setSseChatData(payload);
      },
      [],
    );

    const sseUrl = `${API_URL}/api/sse/subscribe`;
  
    const { messageVisitId } = useChatSse({
      visitId,
      sseUrl,
      onNotification: handleSseNotification,
      userId: currentUserId
    });

    useEffect(() => {
      if(Object.keys(messageVisitId).length > 0 && visitId) {
        setUnreadTotal((messageVisitId?.[visitId]));
      }
    }, [messageVisitId]);

  // useEffect(() => {
  //   setSelectedPreAuthStep("");
  //   setSelectedDocumentType(null);
  //   setExpandedSection(null);
  //   setExpandedSection2(null);
  // }, [open]);
  useEffect(() => {
    if (!open) {
      setSelectedPreAuthStep("");
      setSelectedDocumentType(null);
      // Only reset expandedSection if there's no stepNumber prop
      if (!props.stepNumber) {
        setExpandedSection(null);
      }
    } else if (props.stepNumber) {
      // When opening the drawer, set the expandedSection based on stepNumber
      const stage = getStageFromStepNumber(props.stepNumber);
      if (stage) {
        setExpandedSection(stage);
      }
    }
  }, [open, props.stepNumber]);

  const toggleSection = (section: string) => {
    setExpandedSection((prev) => (prev === section ? null : section));
  };
  // const wrapperRef = useRef<HTMLDivElement>(null);

  const downloadAttachments = async (stageId: number) => {
    if (props?.visitId) {
      const downloadUrl = await downloadAllAttachments(props?.visitId, stageId);
      if (downloadUrl == "success") {
        message.success(`Attachmets downloaded successfully`);
      } else {
        message.error(`Failed to download attachmets`);
      }
    }
  };

  const fetchDocuments = async (
    schemeMetaDataId: number,
    caseId: number,
    visitId: number
  ) => {
    try {
      setLoading(true);
      // const documentList = await getDocuments(props?.schemeMetaDataId);
      if (props?.caseId && props?.visitId) {
        const AllDocumentList = await getAllStarhealthDocumentsData(
          caseId,
          visitId,
          schemeMetaDataId
        );
        setDocumentTypes(AllDocumentList);
        setUploadedDocumentUrl({});
      }
    } catch (err) {
      // errorMessage("Please, Try again after sometime");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (props?.schemeMetaDataId && props?.caseId && props?.visitId) {
      // fetchDocuments(props?.schemeMetaDataId, props?.caseId, props?.visitId);
    }
  }, [props?.schemeMetaDataId, props?.caseId, props?.visitId, open]);

  useEffect(() => {
    if (documentTypes && Object.keys(documentTypes)?.length > 0) {
      getAttachmentsWithValues(documentTypes);
      structureDocumentList(documentTypes);
    }
  }, [documentTypes]);

  // const handleClickOutside = (event: MouseEvent) => {
  //   if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
  //     setPreviewDocument(false);
  //   }
  // };

  // useEffect(() => {
  //   document.addEventListener("mousedown", handleClickOutside);
  //   return () => {
  //     document.removeEventListener("mousedown", handleClickOutside);
  //   };
  // }, []);

  const preAuthSteps =
    preAuth?.stepperData?.visitStages &&
    preAuth?.stepperData?.visitStages
      ?.filter(
        (item: any) =>
          item.visitStage.toLowerCase() !== "patient registration" &&
          item.visitStage.toLowerCase() !== "portal registration" &&
          item.visitStage.toLowerCase() !== "invoice & reconciliation" &&
          item.visitStage.toLowerCase() !== "preauth query" &&
          item.visitStage.toLowerCase() !== "enhancement"
      )
      ?.map((item: any) => item?.visitStage);

  const mapAttachmentToDocList = (attachment: DocumentData) => {
    const {
      documentId,
      documentName,
      data,
      labelId,
      label,
      deficiencyCheckStatus,
      status,
    } = attachment;
    if (data && documentName) {
      return {
        documentId,
        documentName,
        documentUrl: data,
        documentType: labelId,
        deficiencyCheckStatus,
        documentLabelType: label,
        status,
      };
    } else {
      return null;
    }
  };

  const groupByIdAndCreateDocList = (data: DocumentData[]) => {
    const grouped = data.reduce((acc: any, attachment) => {
      // const { labelId } = attachment;
      if (!acc[attachment?.labelId]) {
        acc[attachment?.labelId] = {
          ...attachment,
          docList: [],
        };
      }

      mapAttachmentToDocList(attachment) &&
        acc[attachment?.labelId].docList.push(
          mapAttachmentToDocList(attachment)
        );

      return acc;
    }, {});

    return Object.values(grouped);
  };

  const structureDocumentList = (documentData: any) => {
    const result = Object.keys(documentData).reduce((acc: any, key) => {
      const matchKey =
        preAuthSteps &&
        preAuthSteps
          .find((dataKey: any) => {
            const cleanItem = dataKey
              .replace(/[^a-zA-Z0-9]/g, "")
              .toLowerCase();
            return key.toLowerCase().includes(cleanItem);
          })
          ?.replace(/[^a-zA-Z0-9]/g, "")
          .toLowerCase();
      if (matchKey)
        acc[matchKey] =
          documentData[key] && groupByIdAndCreateDocList(documentData[key]);
      return acc;
    }, {});

    setStructuredDocumentList(result);
  };

  const getAttachmentsWithValues = (documentData: any) => {
    const uploadedDocuments: Record<string, IDocumentFields[]> = {};
    Object.keys(documentData)?.forEach((item) => {
      const attachmentsWithValues: Array<IDocumentFields> = [];
      documentData[item]?.forEach((docType: any) => {
        if (docType?.documentList && docType?.documentList?.length > 0) {
          attachmentsWithValues.push(
            ...docType?.documentList?.map((item: any) => ({
              ...item,
              status: docType?.status,
              updatable: docType?.updatable,
            }))
          );
        }
      });

      const matchKey =
        preAuthSteps &&
        preAuthSteps
          .find((dataKey: any) => {
            // const cleanItem = dataKey.toLowerCase();
            return item.toLowerCase() == dataKey.toLowerCase();
          })
          ?.replace(/[^a-zA-Z0-9]/g, "")
          .toLowerCase();
      // const matchKey = preAuthSteps
      // .find((dataKey) => {
      //   const cleanItem = dataKey.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
      //   return item.toLowerCase().includes(cleanItem);
      // })
      // ?.replace(/[^a-zA-Z0-9]/g, "")
      // .toLowerCase();

      if (matchKey) uploadedDocuments[matchKey] = attachmentsWithValues;
    });
    // setUploadedDocumentUrl(uploadedDocuments);
    setUploadedDocumentUrlList(uploadedDocuments);
  };

  const openFirstPreview = () => {
    setPreviewDocument(true);
    setOpen(false);
  };


  const openSecondPreview = () => {
    setSecondDocument(true);
    setOpen(false);
  };

  const handleSetDocumentUrl = (url: string) => {
    if (!documentUrl) {
      setDocumentUrl(url);
      openFirstPreview();
    } else if (!secondDocumentUrl) {
      setSecondDocumentUrl(url);
      openSecondPreview();
    } else {
      message.info("You can only preview two documents at once");
    }
  };

  const handleCancelPreview = () => {
    setPreviewDocument(false);
    setDocumentUrl("");
    showDrawer();
  };

  const handleCancelPreviewSecondDocument = () => {
    setSecondDocument(false);
    setSecondDocumentUrl("");
    showDrawer();
  };

  const handleSaveData = async (
    uploadDocPayload: Record<string, IDocumentFields[]>
  ) => {
    if (uploadDocPayload) {
      setSaveBtnLoading(true);

      const payloadSchema: {
        casePreAuthAttachments: any[];
        caseEnhancementsAttachments: any[];
        caseDischargeAttachments: any[];
        caseClaimAttachments: any[];
      } = {
        casePreAuthAttachments: [],
        caseEnhancementsAttachments: [],
        caseDischargeAttachments: [],
        caseClaimAttachments: [],
      };

      Object.keys(payloadSchema)?.forEach((payloadKey) => {
        const matchKey = Object.keys(uploadDocPayload)?.find((dataKey) =>
          payloadKey.toLowerCase().includes(dataKey)
        );

        if (matchKey) {
          // Use 'as keyof typeof payloadSchema' to assert the type of payloadKey
          payloadSchema[payloadKey as keyof typeof payloadSchema] =
            uploadDocPayload[matchKey];
        }
      });

      if (props?.caseId && props?.visitId && props?.schemeMetaDataId) {
        try {
          const AllDocumentList = await updateAllDocumentsData(
            props?.caseId,
            props?.visitId,
            payloadSchema
          );
          message.success(`Uploaded documents saved successfully`);
          fetchDocuments(
            props?.schemeMetaDataId,
            props?.caseId,
            props?.visitId
          );
        } catch (error) {
          message.error(`Something went worng`);
        } finally {
          setSaveBtnLoading(false);
        }
      }
    }
  };

  const handleDownloadDocument = async (fileName: string, url: string) => {
    try {
      setDownloadingDocumentUrl(url);
      setIsDownloadingDocument(true);
      await downloadDocument(fileName, url);
      successMessage("Document Downloaded Successfully");
      setIsDownloadingDocument(false);
      setDownloadingDocumentUrl("");
    } catch (err) {
      errorMessage("Please, Try again later");
      setIsDownloadingDocument(false);
      setDownloadingDocumentUrl("");
    }
  };

  const handleDeleteList = async (documentUrl: string, cleanItem: string) => {
    const deleteDocument = uploadedDocumentUrlList[cleanItem]?.find(
      (doc, index) => doc?.documentUrl === documentUrl
    );

    const deleteForm = {
      documentUrl: deleteDocument?.documentUrl,
      status: false,
      id: deleteDocument?.id,
    };
    const newUpdatedDocumentList = uploadedDocumentUrlList[cleanItem]?.filter(
      (doc, index) => doc?.documentUrl !== documentUrl
    );
    // setUploadedDocumentUrl({
    //   ...uploadedDocumentUrl,
    //   [cleanItem]: newUpdatedDocumentList,
    // });
    setUploadedDocumentUrlList({
      ...uploadedDocumentUrlList,
      [cleanItem]: newUpdatedDocumentList,
    });

    if (deleteDocument?.documentUrl) {
      try {
        await dispatch(deleteUploadedDocument(deleteForm));
      } catch (err) {
        console.error(err);
      }
    }
    // if (dischargeData?.cghsDischargeResponse) {
    //   const remainingDocsOfType = newUpdatedDocumentList?.filter(
    //     (doc) => doc.documentLabelType === documentType
    //   );
    //   if (remainingDocsOfType.length === 0) {
    //     dispatch(
    //       updateField({
    //         sectionKey: "cghsDischargeResponse",
    //         fieldId: documentType.split(" ").join("_"),
    //         value: "",
    //       })
    //     );
    //     dispatch(
    //       updateDischargeDataField({
    //         sectionKey: "cghsDischargeResponse",
    //         fieldId: documentType.split(" ").join("_"),
    //         updateValues: {
    //           data: "",
    //           documentName: "",
    //         },
    //       })
    //     );
    //   }
    // }
  };

  const handlePreAuthStepChange = (event: any) => {
    const value = event.target.value;
    // const selectedStageDocList = value && getDocumentOptions(value);
    // if (
    //   selectedStageDocList.length &&
    //   selectedStageDocList[0]?.status
    //     ?.toLowerCase()
    //     ?.includes("completed on portal")
    // ) {
    //   message.warning(`${selectedStageDocList[0]?.status}`);
    //   setSelectedPreAuthStep("");
    // } else {
    //   setSelectedPreAuthStep(value);
    // }

    setSelectedPreAuthStep(value);
    setSelectedDocumentType(null);
  };
  const handleDocumentTypeChange = (event: any) => {
    const value = event.target.value;
    const cleanItem =
      selectedPreAuthStep &&
      selectedPreAuthStep.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
    const selectedDoc: Array<DocumentData> =
      selectedPreAuthStep &&
      getDocumentOptions(selectedPreAuthStep)?.filter(
        (doc: DocumentData) => doc.labelId == value
      );

    const matchInUploadedDocs =
      cleanItem &&
      uploadedDocumentUrlList[cleanItem]?.find(
        (item) => item.documentType == value
      );
    // if (selectedDoc && !selectedDoc[0]?.multiple && selectedDoc[0]?.data) {
    //   message.warning(`Document already present for ${selectedDoc[0]?.label}`);
    //   setSelectedDocumentType(null);
    // }
    // else if (
    //   selectedDoc &&
    //   !selectedDoc[0]?.multiple &&
    //   matchInUploadedDocs
    // ) {
    //   message.warning(`Document already present for ${selectedDoc[0]?.label}`);
    //   setSelectedDocumentType(null);
    // }
    // else {
    const selectedDocType = {
      label: selectedDoc[0]?.label,
      value: selectedDoc[0]?.labelId,
    };
    setSelectedDocumentType(selectedDocType || null);
    // }
  };

  const getDocumentOptions = (selectedStage: string) => {
    if (!selectedStage || Object.keys(documentTypes)?.length < 1) {
      return [];
    } else {
      // const cleanItem = selectedStage
      //   .replace(/[^a-zA-Z0-9]/g, "")
      //   .toLowerCase();
      const matchingKey = Object.keys(documentTypes)?.find((key) =>
        key.toLowerCase().includes(selectedStage.toLowerCase())
      );
      const stepDocumentList: any = matchingKey && documentTypes[matchingKey];
      return matchingKey ? stepDocumentList : [];
    }
  };

  const viewFile = (file: any) => {
    window.open(file, "_blank");
  };

  const updateOrAddDocument = (
    selectedPreAuthStep: string,
    uploadedDocumentUrl: Record<string, Array<IDocumentFields>>,
    documentRes: IDocumentFields[]
  ): Record<string, Array<IDocumentFields>> => {
    const cleanItem = selectedPreAuthStep
      .replace(/[^a-zA-Z0-9]/g, "")
      .toLowerCase();

    const isSpecialLabelType = (labelType: string | undefined) => {
      if (!labelType) return false;
      const upperLabel = labelType.toUpperCase();
      return (
        upperLabel === "BILL ATTACHMENTS" ||
        upperLabel === "INVESTIGATION REPORTS" ||
        upperLabel === "CGHS BILL" ||
        upperLabel === "CONSULTATION NOTES" ||
        upperLabel === "CASE SHEET" ||
        upperLabel === "CONSUMABLE DOCUMENT" ||
        upperLabel === "DRUG CERTIFICATE" ||
        upperLabel === "IMPLANT BILL ATTACHMENTS" ||
        upperLabel === "IMPLANT INVOICE" ||
        upperLabel === "JUSTIFICATION FOR UNSPECIFIED PACKAGE" ||
        upperLabel === "PROCEDURE NOTES" ||
        upperLabel === "POST PROCEDURE X RAY" ||
        upperLabel === "CLINICAL NOTES"
      );
    };

    let updatedDocuments = uploadedDocumentUrl[cleanItem]
      ? [...uploadedDocumentUrl[cleanItem]]
      : [];

    if (
      (patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
        "CGHS" ||
        patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
          "CAPF") &&
      cleanItem?.toLowerCase()?.includes("claim")
    ) {
      documentRes?.forEach((docRes) => {
        const documentIndex = updatedDocuments.findIndex(
          (doc) => doc?.documentType === docRes?.documentType
        );
        if (
          documentIndex !== -1 &&
          !isSpecialLabelType(docRes?.documentLabelType)
        ) {
          updatedDocuments[documentIndex] = {
            ...updatedDocuments[documentIndex],
            documentName: docRes?.documentName,
            documentUrl: docRes?.documentUrl,
            deficiencyCheckStatus: docRes?.deficiencyCheckStatus,
            id: docRes?.id,
          };
        } else {
          updatedDocuments.push({
            documentType: docRes?.documentType,
            documentName: docRes?.documentName,
            documentUrl: docRes?.documentUrl,
            documentLabelType: docRes?.documentLabelType,
            id: docRes?.id,
            deficiencyCheckStatus: docRes?.deficiencyCheckStatus,
          });
        }
      });
      return { ...uploadedDocumentUrl, [cleanItem]: updatedDocuments };
    } else if (
      patients?.fetchBasicDetails?.casesResponse?.portalMetaDataNames?.[0]?.toUpperCase() ===
      "IHX"
    ) {
      documentRes?.forEach((docRes) => {
        updatedDocuments.push({
          documentType: docRes?.documentType,
          documentName: docRes?.documentName,
          documentUrl: docRes?.documentUrl,
          documentLabelType: docRes?.documentLabelType,
          id: docRes?.id,
          deficiencyCheckStatus: docRes?.deficiencyCheckStatus,
        });
      });
      return { ...uploadedDocumentUrl, [cleanItem]: updatedDocuments };
    } else {
      documentRes?.forEach((docRes) => {
        const documentIndex = updatedDocuments?.findIndex(
          (doc) => doc?.documentType === docRes?.documentType
        );
        if (documentIndex !== -1) {
          updatedDocuments[documentIndex] = {
            ...updatedDocuments[documentIndex],
            documentName: docRes?.documentName,
            documentUrl: docRes?.documentUrl,
            deficiencyCheckStatus: docRes?.deficiencyCheckStatus,
            id: docRes?.id,
          };
        } else {
          updatedDocuments.push({
            documentType: docRes?.documentType,
            documentName: docRes?.documentName,
            documentUrl: docRes?.documentUrl,
            documentLabelType: docRes?.documentLabelType,
            id: docRes?.id,
            deficiencyCheckStatus: docRes?.deficiencyCheckStatus,
          });
        }
      });
      return { ...uploadedDocumentUrl, [cleanItem]: updatedDocuments };
    }
  };

  // const updateOrAddDocument = (
  //   selectedPreAuthStep: string,
  //   uploadedDocumentUrl: Record<string, Array<IDocumentFields>>,
  //   documentRes: IDocumentFields
  // ): Record<string, Array<IDocumentFields>> => {
  //   const cleanItem = selectedPreAuthStep
  //     .replace(/[^a-zA-Z0-9]/g, "")
  //     .toLowerCase();

  //   if (
  //     (patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
  //       "CGHS" ||
  //       patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
  //         "CAPF") &&
  //     cleanItem?.toLowerCase()?.includes("claim")
  //   ) {
  //     const updatedDocuments = uploadedDocumentUrl[cleanItem]
  //       ? [...uploadedDocumentUrl[cleanItem]]
  //       : [];

  //     const documentIndex = updatedDocuments.findIndex(
  //       (doc) => doc?.documentType === documentRes?.documentType
  //     );
  //     if (
  //       documentIndex !== -1 &&
  //       documentRes?.documentLabelType?.toUpperCase() !== "BILL ATTACHMENTS" &&
  //       documentRes?.documentLabelType?.toUpperCase() !==
  //         "INVESTIGATION REPORTS" &&
  //       documentRes?.documentLabelType?.toUpperCase() !== "CGHS BILL" &&
  //       documentRes?.documentLabelType?.toUpperCase() !==
  //         "CONSULTATION NOTES" &&
  //       documentRes?.documentLabelType?.toUpperCase() !== "CASE SHEET" &&
  //       documentRes?.documentLabelType?.toUpperCase() !==
  //         "CONSUMABLE DOCUMENT" &&
  //       documentRes?.documentLabelType?.toUpperCase() !== "DRUG CERTIFICATE" &&
  //       documentRes?.documentLabelType?.toUpperCase() !==
  //         "Implant Bill Attachments"?.toUpperCase() &&
  //       documentRes?.documentLabelType?.toUpperCase() !==
  //         "IMPLANT INVOICE"?.toUpperCase() &&
  //       documentRes?.documentLabelType?.toUpperCase() !==
  //         "Justification for unspecified package"?.toUpperCase() &&
  //       documentRes?.documentLabelType?.toUpperCase() !==
  //         "PROCEDURE NOTES"?.toUpperCase() &&
  //       documentRes?.documentLabelType?.toUpperCase() !==
  //         "Post Procedure X Ray"?.toUpperCase() &&
  //       documentRes?.documentLabelType?.toUpperCase() !==
  //         "CLINICAL NOTES"?.toUpperCase()
  //     ) {
  //       updatedDocuments[documentIndex] = {
  //         ...updatedDocuments[documentIndex],
  //         documentName: documentRes?.documentName,
  //         documentUrl: documentRes?.documentUrl,
  //         deficiencyCheckStatus: documentRes?.deficiencyCheckStatus,
  //         id: documentRes?.id,
  //       };
  //     } else {
  //       updatedDocuments.push({
  //         documentType: documentRes?.documentType,
  //         documentName: documentRes?.documentName,
  //         documentUrl: documentRes?.documentUrl,
  //         documentLabelType: documentRes?.documentLabelType,
  //         id: documentRes?.id,
  //         deficiencyCheckStatus: documentRes?.deficiencyCheckStatus,
  //       });
  //     }

  //     return { ...uploadedDocumentUrl, [cleanItem]: updatedDocuments };
  //   } else if (
  //     patients?.fetchBasicDetails?.casesResponse?.portalMetaDataNames?.[0]?.toUpperCase() ===
  //     "IHX"
  //     // && (cleanItem?.toLowerCase()?.includes("claim") ||
  //     // cleanItem?.toLowerCase()?.includes("preauth"))
  //   ) {
  //     const updatedDocuments = uploadedDocumentUrl[cleanItem]
  //       ? [...uploadedDocumentUrl[cleanItem]]
  //       : [];

  //     const documentIndex = updatedDocuments.findIndex(
  //       (doc) => doc?.documentType === documentRes?.documentType
  //     );
  //     // if (
  //     //   documentIndex !== -1 &&
  //     //   documentRes?.documentLabelType?.toUpperCase() !== "OTHERS"
  //     // ) {
  //     //   updatedDocuments[documentIndex] = {
  //     //     ...updatedDocuments[documentIndex],
  //     //     documentName: documentRes?.documentName,
  //     //     documentUrl: documentRes?.documentUrl,
  //     //     deficiencyCheckStatus: documentRes?.deficiencyCheckStatus,
  //     //     id: documentRes?.id,
  //     //   };
  //     // } else {
  //     updatedDocuments.push({
  //       documentType: documentRes?.documentType,
  //       documentName: documentRes?.documentName,
  //       documentUrl: documentRes?.documentUrl,
  //       documentLabelType: documentRes?.documentLabelType,
  //       id: documentRes?.id,
  //       deficiencyCheckStatus: documentRes?.deficiencyCheckStatus,
  //     });
  //     // }

  //     return { ...uploadedDocumentUrl, [cleanItem]: updatedDocuments };
  //   } else {
  //     const updatedDocuments = uploadedDocumentUrl[cleanItem]
  //       ? [...uploadedDocumentUrl[cleanItem]]
  //       : [];

  //     const documentIndex = updatedDocuments.findIndex(
  //       (doc) => doc?.documentType === documentRes?.documentType
  //     );

  //     if (documentIndex !== -1) {
  //       updatedDocuments[documentIndex] = {
  //         ...updatedDocuments[documentIndex],
  //         documentName: documentRes?.documentName,
  //         documentUrl: documentRes?.documentUrl,
  //         deficiencyCheckStatus: documentRes?.deficiencyCheckStatus,
  //         id: documentRes?.id,
  //       };
  //     } else {
  //       updatedDocuments.push({
  //         documentType: documentRes?.documentType,
  //         documentName: documentRes?.documentName,
  //         documentUrl: documentRes?.documentUrl,
  //         documentLabelType: documentRes?.documentLabelType,
  //         id: documentRes?.id,
  //         deficiencyCheckStatus: documentRes?.deficiencyCheckStatus,
  //       });
  //     }

  //     // Return the updated array (ensures it's a flat array)
  //     return { ...uploadedDocumentUrl, [cleanItem]: updatedDocuments };
  //   }
  // };

  const handleSelectedDocument =
    (documentType: any, stage: string) => (e: any) => {
      const cleanItem = stage.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
      if (Array.isArray(e)) {
        return e;
      }
      if (
        props?.schemeMetaDataName?.toLocaleUpperCase().includes("ECHS") &&
        e?.fileList[0]?.type !== "application/pdf"
      ) {
        errorMessageModal("File Must be PDF only");
        return;
      }
      if (
        documentType?.value &&
        (documentType?.value?.toUpperCase() === "24" ||
          documentType?.value?.toUpperCase() === "1310"?.toUpperCase()) &&
        e?.fileList[0]?.type !== "application/pdf"
      ) {
        errorMessageModal("File Must be PDF only");
        return;
      }

      if (e?.fileList[0] && documentType) {
        dispatch(
          setSelectedFile({
            file: e?.file,
            documentType:
              documentType?.labelId ||
              documentType?.documentType ||
              documentType?.value,
            stage: cleanItem,
          })
        );

        setPreviewFile({
          stage: cleanItem,
          url: URL.createObjectURL(e.file),
          name: e.file.name,
          showUpload: true,
          documentType:
            documentType?.labelId ||
            documentType?.documentType ||
            documentType?.value,
        });
        setPreviewModalVisible(true);
      }
      setSelectedDocumentType(null);
    };

  const handleUpload = async () => {
    if (!patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
      return;
    }
    const CollectPostData = new FormData();
    if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
      CollectPostData.append(
        "hospitalId",
        patients?.fetchBasicDetails?.casesResponse?.healthCareId?.toString()
      );
    }
    if (patients?.fetchBasicDetails?.patientResponse?.id) {
      CollectPostData.append(
        "patientId",
        patients?.fetchBasicDetails?.patientResponse?.id?.toString()
      );
    }
    if (patients?.fetchBasicDetails?.casesResponse?.id) {
      CollectPostData.append(
        "caseId",
        patients?.fetchBasicDetails?.casesResponse?.id.toString()
      );
    }
    if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
      CollectPostData.append(
        "visitId",
        patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString()
      );
    }

    CollectPostData.append("documentType", previewFile.documentType);

    if (selectedFile?.file) {
      CollectPostData.append("file", selectedFile?.file);
    }

    try {
      const documentRes = await dispatch(
        UploadDocument({
          formData: CollectPostData,
          accessToken: auth.accessToken || "",
        })
      ).unwrap();

      if (previewFile.stage && documentRes?.length > 0) {
        const stage = previewFile.stage;
        const updatedDocuments = updateOrAddDocument(
          stage,
          uploadedDocumentUrl,
          documentRes
        );
        setUploadedDocumentUrl(updatedDocuments);
        // setUploadedDocumentUrlList(updatedDocuments);
        handleSaveData(updatedDocuments);

        // message.success(`Document uploaded successfully`);
      } else {
        message.error(`Document upload failed`);
      }

      setPreviewModalVisible(false);
      setSelectedPreAuthStep("");
      setSelectedDocument(null);
      setSelectedFile(null);
      setSelectedDocumentType(null);

      const currentPath = window.location.pathname;
      if (currentPath.includes("billing-list")) {
        setTimeout(async () => {
          await props?.getBillList();
        }, 500);
      }
    } catch (error) {
      console.error("Failed to upload document:", error);
    }
  };

  const removeDuplicateLabelIds = (attachments: any) => {
    const uniqueAttachments: any = {};
    attachments?.forEach((attachment: any) => {
      if (!uniqueAttachments[attachment.labelId]) {
        uniqueAttachments[attachment.labelId] = attachment;
      }
    });
    return Object.values(uniqueAttachments);
  };

  // const getDocumentOptions = (step: string) => {
  //   if (!step || !props?.structuredDocumentList) return [];
  //   const cleanItem = step.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
  //   const matchingKey = Object.keys(props?.structuredDocumentList)?.find((key) =>
  //     key.toLowerCase().includes(cleanItem)
  //   );
  //   return matchingKey ? props?.structuredDocumentList[matchingKey] : [];
  // };

  const uploadProps = {
    name: "file",
    multiple: false,
    // showUploadList: false,
    // action: "", // Replace with your upload endpoint
    onChange(info: any) {
      if (selectedPreAuthStep && selectedDocumentType) {
        handleSelectedDocument(selectedDocumentType, selectedPreAuthStep)(info);
      }
    },
  };

  // const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
  // const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
  // const currentUrl = window.location.href;

  useEffect(() => {
    if (currentUrl.includes("visit-details")) {
      if (caseId && visitId) {
        // dispatch(fetchPreAuthStepper({ caseId: caseId, visitId: visitId }));
      }
    }
  }, [caseId, visitId]);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isData, setIsData] = useState<{
    documentUrl: string;
    cleanItem: string;
  }>({
    documentUrl: "",
    cleanItem: "",
  });

  const handleOk = () => {
    handleDeleteList(isData?.documentUrl, isData?.cleanItem);
    setIsModalOpen(false);
  };
  const handleCancel = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      {!chatterVisible && !isChatMinimized && (
        // <DraggableButton
        //   showTimeline={showTimeline}
        //   showDrawer={showDrawer}
        //   showChatter={showChatter}
        // />
        // <FloatingChatButton showChatter={showChatter} />
        <div
          style={{
            position: "fixed",
            right: 30,
            bottom: 26,
            zIndex: 1000,
            width: 48,
            height: 48,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ position: "relative", width: 48, height: 48 }}>
            <FloatingChatButton showChatter={showChatter} />
            <span
              style={{
                position: "absolute",
                top: -21,
                right: 24,
                zIndex: 999999,
              }}
            >
              <Badge
                count={unreadTotal > 99 ? "99+" : unreadTotal}
                overflowCount={99}
                style={{ backgroundColor: "#e02424" }}
              />
            </span>
          </div>
        </div>
      )}

      {/* <DraggableFloatingButton showDrawer={showDrawer} /> */}
      {/* <FloatingButton/> */}

      {/* Drawer */}

      <Drawer
        placement="right"
        width={isChatCollapsed ? 56 : 520}
        mask={true}
        maskClosable={true}
        onClose={closeChatter}
        bodyStyle={{ padding: "0px", overflow: "hidden" }}
        style={{
          boxShadow: " 0px -1px 4px 0px #00000040 inset",
          borderRadius: "10px",
          overflow: "hidden",
        }}
        headerStyle={{
          // backgroundColor: "#E5F3F3",
          backgroundColor: "rgb(178 219 255)",
          borderBottom: "0.4px solid rgb(217, 217, 217)",
          padding: isChatCollapsed ? "8px 10px" : "13px 24px",
          // boxShadow: " 0px 4px 20px 0px #00000040",
        }}
        visible={chatterVisible}
        // headerStyle={{ display: "none" }} // Hides the header
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: isChatCollapsed ? "center" : "space-between",
              overflow: "hidden",
              gap: 8,
            }}
          >
            {!isChatCollapsed && (
              <span
                style={{
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Chat
              </span>
            )}
            {/* <Tooltip
              title={isChatCollapsed ? "Expand" : "Collapse"}
              placement="left"
              destroyTooltipOnHide
              getPopupContainer={() => document.body}
            >
              <Button
                type="text"
                size="small"
                aria-label={isChatCollapsed ? "Expand chat" : "Collapse chat"}
                onClick={toggleChatCollapsed}
                icon={isChatCollapsed ? <LeftOutlined /> : <RightOutlined />}
                style={{ width: 32, height: 32, padding: 0, minWidth: 0 }}
              />
            </Tooltip> */}
            {!isChatCollapsed && (
              <Tooltip
                title="Minimize"
                placement="left"
                destroyTooltipOnHide
                getPopupContainer={() => document.body}
              >
                <Button
                  type="text"
                  size="small"
                  aria-label="Minimize chat"
                  onClick={minimizeChat}
                  icon={<DownOutlined />}
                  style={{ width: 32, height: 32, padding: 0, minWidth: 0 }}
                />
              </Tooltip>
            )}
          </div>
        }
      >
        {isChatCollapsed ? (
          <div
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <Button
              type="primary"
              size="small"
              onClick={toggleChatCollapsed}
              icon={<LeftOutlined />}
              style={{ width: 32, height: 32, padding: 0, minWidth: 0 }}
            />
          </div>
        ) : (
          <Chatter
            handleNotificationCountChange={handleNotificationCountChange}
          />
        )}
      </Drawer>

      {/* {isChatMinimized && (
        <Button
          type="primary"
          shape="circle"
          size="large"
          aria-label="Open chat"
          onClick={restoreChat}
          icon={<MessageOutlined />}
          style={{
            position: "fixed",
            right: 16,
            bottom: 16,
            zIndex: 1000,
            width: 48,
            height: 48,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        />
      )} */}

      {isChatMinimized && (
        // <div style={{ position: "fixed", right: 30, bottom: 26, zIndex: 1000 }}>
        //   <Badge
        //     count={unreadTotal > 99 ? "99+" : unreadTotal}
        //     overflowCount={99}
        //     style={{ backgroundColor: "#e02424" }}
        //   >
        //     <Button
        //       type="primary"
        //       shape="circle"
        //       size="large"
        //       aria-label="Open chat"
        //       onClick={restoreChat}
        //       icon={<MessageOutlined />}
        //       style={{
        //         width: 48,
        //         height: 48,
        //         display: "flex",
        //         alignItems: "center",
        //         justifyContent: "center",
        //       }}
        //     />
        //   </Badge>
        // </div>

        <div
          style={{
            position: "fixed",
            right: 86,
            bottom: 36,
            zIndex: 1000,
            width: 48,
            height: 48,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ position: "relative", width: 48, height: 48 }}>
            <div className="floating-button" onClick={restoreChat}>
              <MessageOutlined />
              <span className="label">Chat</span>
            </div>
            <span
              style={{
                position: "absolute",
                top: -15,
                right: -54,
                zIndex: 999999,
              }}
            >
              <Badge
                count={unreadTotal > 99 ? "99+" : unreadTotal}
                overflowCount={99}
                style={{ backgroundColor: "#e02424" }}
              />
            </span>
          </div>
        </div>
      )}
      <ConfirmModals
        setIsModalOpen={setIsModalOpen}
        isModalOpen={isModalOpen}
        handleOk={handleOk}
        handleCancel={handleCancel}
        text={"ARE YOU SURE YOU WANT TO DELETE THIS DOCUMENT"}
      />
    </>
  );
};

export default FloatingDrawer;
